import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BladeStackProvider, useBladeStack } from './BladeStackProvider';
import { BladeDataProvider } from './BladeDataSource';
import { BladeActionProvider } from './BladeActionSource';
import { mockSource } from './sources/mockSource';
import { mockActionSource } from './sources/mockActionSource';
import { AuditDataProvider } from '../audit/AuditDataSource';
import { auditSource } from '../audit/sources/auditSource';
import { AUDIT_DEFAULT_STACK } from '../audit/stack';
import { WorkbenchShell } from './WorkbenchShell';
import type { BladeStackState } from './types';

// Where-was-I restoration (HUB-504 polish). A hot-reload or a manual browser
// reload otherwise re-seeds the backendless preview back to the batch queue,
// disrupting an in-progress review. We mirror the blade stack into sessionStorage
// and rehydrate from it on mount, so a reload lands the auditor back on the batch
// detail (or the exact case they were reviewing). sessionStorage (not local) means
// it survives reloads within the tab session but never leaks across sessions, and
// it is entirely client-side — no backend, and crucially no /workbench/login
// redirect (the preview route is unauthenticated). Ephemeral blades (e.g. search
// results, which may carry a PHI query) are stripped before persisting.
const STACK_STORAGE_KEY = 'cw_audit_preview_stack';

function readRestoredStack(): BladeStackState {
  try {
    const raw = sessionStorage.getItem(STACK_STORAGE_KEY);
    if (!raw) return AUDIT_DEFAULT_STACK;
    const parsed = JSON.parse(raw) as BladeStackState;
    if (
      parsed &&
      Array.isArray(parsed.blades) &&
      parsed.blades.length > 0 &&
      parsed.blades.every((b) => b && typeof b.key === 'string' && typeof b.title === 'string')
    ) {
      return parsed;
    }
  } catch {
    /* malformed / storage unavailable — fall back to the default landing stack */
  }
  return AUDIT_DEFAULT_STACK;
}

/** Mirrors the live blade stack into sessionStorage on every change (preview-only).
 *  Renders nothing. Ephemeral blades are dropped and the active index re-clamped. */
function StackRestorer() {
  const { state } = useBladeStack();
  useEffect(() => {
    try {
      const blades = state.blades.filter((b) => !b.ephemeral);
      const persist: BladeStackState = {
        blades,
        mode: state.mode,
        activeIndex: Math.min(state.activeIndex, blades.length - 1),
      };
      sessionStorage.setItem(STACK_STORAGE_KEY, JSON.stringify(persist));
    } catch {
      /* storage unavailable (private mode / tests) — non-fatal */
    }
  }, [state]);
  return null;
}

// Standalone, seeded preview of the blade-canvas shell (HUB-275), mounted at
// /workbench/shell-preview — no backend. Self-contained: its own QueryClient plus
// the static mock data sources, so primitives render sample data without the live
// @hub/api. The URL-synced, live-data version lives at /w/* (WorkbenchShellRoute).
//
// The preview lands on the coding-audit batch queue (HUB-504) and wires the audit
// data seam alongside the generic blade source. The sidebar's coding-queue entry
// swaps back to the generic list → record → form demo stack.
export function ShellPreview() {
  const [client] = useState(
    () => new QueryClient({ defaultOptions: { queries: { retry: false } } }),
  );
  const [initialStack] = useState(readRestoredStack);
  return (
    <QueryClientProvider client={client}>
      <BladeDataProvider source={mockSource}>
        <BladeActionProvider source={mockActionSource}>
          <AuditDataProvider source={auditSource}>
            <BladeStackProvider initial={initialStack}>
              <StackRestorer />
              <WorkbenchShell enableAuditNav />
            </BladeStackProvider>
          </AuditDataProvider>
        </BladeActionProvider>
      </BladeDataProvider>
    </QueryClientProvider>
  );
}
